<script setup lang="ts">
import { NxDialog } from './../Dialog';
import { NxButton } from './../Button';
import { computed, inject, ref, watch } from 'vue';
import NxColumnVisibilityList, { type ColumnVisibilityItem } from './NxColumnVisibilityList.vue';
import { i18nInjectionKey } from '../internal';

type ColumnVisibility = { displayName: string; visible: boolean; sticky?: boolean };
const isDialogOpen = defineModel<boolean>('open', { required: true });
const props = defineProps<{ initialVisibility: ColumnVisibility[]; showSticky?: boolean }>();

const items = ref<ColumnVisibilityItem[]>(toItems(props.initialVisibility));
// needed as the dialog is only mounted once/not dismounted when closed
watch(
  () => props.initialVisibility,
  (next) => {
    items.value = toItems(next);
  }
);

function toItems(vis: ColumnVisibility[]): ColumnVisibilityItem[] {
  return vis.map(({ displayName, visible, sticky }) => ({
    key: displayName,
    label: displayName,
    visible,
    sticky: !!sticky,
  }));
}

const emit = defineEmits<{
  submit: [newVisibility: ColumnVisibility[]];
}>();

const i18nInjection = inject(i18nInjectionKey)!;

const submitValue = computed<ColumnVisibility[]>(() =>
  items.value.map(({ label, visible, sticky }) => ({ displayName: label, visible, sticky }))
);
</script>

<template>
  <NxDialog
    v-model:open="isDialogOpen"
    :title="i18nInjection.t('common.dialogs.columns.title')"
    :description="i18nInjection.t('common.dialogs.columns.description')"
    size="default"
    fill
  >
    <template #content>
      <div class="column-dialog-body">
        <NxColumnVisibilityList v-model="items" :show-sticky="props.showSticky" fill />
      </div>
    </template>
    <template #buttons="{ close }">
      <NxButton
        @click="
          () => {
            close();
            emit('submit', submitValue);
          }
        "
      >
        {{ i18nInjection.t('common.dialogs.columns.ok') }}
      </NxButton>
      <NxButton variant="outline" @click="close">
        {{ i18nInjection.t('common.dialogs.columns.cancel') }}
      </NxButton>
    </template>
  </NxDialog>
</template>

<style scoped>
.column-dialog-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  margin-top: var(--nxt-space-2xs);
}
</style>
